import { useDragAndDrop } from "@/components/ui/drag-drop-upload/use-drag-and-drop";
import { cn } from "@/utils/classes";
import { useRef } from "react";
import { Button } from "../button";
import { Table, TableCell, TableHeader, TableRow } from "../table";
import { CheckCircleIcon, ExclamationCircleIcon, EyeSlashIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { EditElement } from "@/features/studies/components/edit-element";
import { asFileSize } from "@/utils/format";
import { Toolbar, ToolbarItem } from "../toolbar";
import { useSegment } from "@/hooks/use-segment";
import { useUploadsStore } from "./use-uploads-store";

/* Constants */
const ACCEPTED_FILE_TYPES = [".jpg", ".jpeg", ".png", ".tiff", ".tif"];

/* Prop Types */
interface DragDropUploadProps {
  additionalSegmentProperties?: Record<string, any>;
  segmentTrackName: string;
  segmentTrackMediaType: string;
  dragDropVideoUrl: string;
  dragDropVideoClassName?: string;
  dragDropText: string;
  selectAssetsText?: string;
  firstColumnHeader?: string;
}

/*
 * DragDropUpload component
 * We standardize the drag and drop upload component for both studies and context uploads
 */
export const DragDropUpload = ({
  additionalSegmentProperties,
  segmentTrackName,
  segmentTrackMediaType,
  dragDropVideoUrl,
  dragDropVideoClassName,
  dragDropText,
  selectAssetsText = "Select assets",
  firstColumnHeader = "Asset",
}: DragDropUploadProps) => {
  /* Refs */
  const ref = useRef<HTMLInputElement>(null);

  /* Stores */
  const uploads = useUploadsStore((state) => state.uploads);
  const { remove, reset } = useUploadsStore((state) => state.uploadActions);

  /* Hooks */
  const { segmentTrackEvent } = useSegment();
  const { isDragging, dragBindings, handleAdd, handleNameSave } = useDragAndDrop({
    inputRef: ref,
    accept: ACCEPTED_FILE_TYPES,
    segmentTrackName,
    segmentTrackMediaType,
    additionalSegmentProperties,
  });

  /* Handlers */
  const handleClick = () => {
    ref.current?.click();
  };

  const handleRemove = (name: string, key: string) => {
    // Segment event for each removed image
    segmentTrackEvent(`${segmentTrackName} Staged ${segmentTrackMediaType} Removed`, {
      ...additionalSegmentProperties,
      fileName: name,
    });
    remove(key);
  };

  const handleClear = () => {
    // Segment event for clearing all staged images
    segmentTrackEvent(`${segmentTrackName} Staged ${segmentTrackMediaType}s Cleared`, {
      ...additionalSegmentProperties,
    });
    reset();
  };

  /* Render */
  return (
    <>
      {/* Tertiary Toolbar */}
      {!!uploads.length && (
        <Toolbar>
          <ToolbarItem>
            <div className="flex flex-row gap-2">
              <Button disabled={!uploads.length} onClick={handleClear}>
                Clear All
              </Button>
              <Button variant="primary" disabled={!uploads.length} onClick={handleClick}>
                <PlusIcon className="size-5 text-neutral" />
                Add
              </Button>
            </div>
          </ToolbarItem>
        </Toolbar>
      )}

      {/* Empty state with drag and drop */}
      {!uploads.length && (
        <div
          className={cn(
            "w-full flex flex-col justify-center gap-6 py-9 h-96 items-center border-2 border-dashed border-base-300 rounded-lg",
            isDragging && "border-primary bg-base-200",
          )}
          {...dragBindings}
        >
          <video src={dragDropVideoUrl} className={cn("w-36", dragDropVideoClassName)} autoPlay muted loop />
          {!isDragging && (
            <div className="flex flex-col gap-3 items-center">
              <h2>Drag & drop</h2>
              <p className="text-base-content/70">{dragDropText}</p>
            </div>
          )}

          {isDragging && <p>Drop files here</p>}
          <Button variant="primary" onClick={handleClick}>
            {selectAssetsText}
          </Button>
        </div>
      )}

      {/* Uploads */}
      {!!uploads.length && (
        <div className="grow flex flex-row flex-wrap w-full gap-4 p-2">
          <Table>
            <thead>
              <TableRow>
                <TableHeader>{firstColumnHeader}</TableHeader>
                <TableHeader className="w-40">Size</TableHeader>
                <TableHeader>Dimensions</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader className="w-20"></TableHeader>
              </TableRow>
            </thead>
            <tbody>
              {uploads?.map((upload) => (
                <TableRow key={upload.key} className="group hover:bg-base-100/50 cursor-pointer">
                  <TableCell truncate={false}>
                    {upload.isValid ? (
                      <img src={URL.createObjectURL(upload.file)} className="rounded-lg w-16 h-16 object-contain" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-base-300 flex items-center justify-center">
                        <EyeSlashIcon className="size-7 text-error" />
                      </div>
                    )}
                    <EditElement defaultValue={upload.name} elementId={upload.name} handleElementEdit={handleNameSave}>
                      <p className="truncate">{upload.name}</p>
                    </EditElement>
                  </TableCell>
                  <TableCell>{asFileSize(upload.file.size)}</TableCell>
                  <TableCell>{`${upload.width} × ${upload.height} px`}</TableCell>
                  <TableCell>
                    <div className={cn("flex items-center gap-2", upload.isValid ? "text-success" : "text-error")}>
                      {upload.isValid ? <CheckCircleIcon className="size-4" /> : <ExclamationCircleIcon className="size-4" />}
                      {upload.isValid ? "Ready" : "Image dimensions are larger than 4096 x 4096px"}
                    </div>
                  </TableCell>
                  <TableCell className="justify-end">
                    <TrashIcon className="size-4" onClick={() => handleRemove(upload.name, upload.key)} />
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Hidden */}
      <input multiple type="file" accept={ACCEPTED_FILE_TYPES.join(", ")} className="hidden" ref={ref} onChange={(e) => handleAdd(Array.from(e.target.files!))} />
    </>
  );
};
